import { get } from "svelte/store";
import { useDataSources } from "../cesium-datasources";
import { useCesiumViewer } from "../cesium-viewer";
import { disableDefaultLeftClickHandler, removeAllScreenSpaceInputActions, toggleEscapeToolListener } from "../map-tools";
import { mapToolsStore } from "../map-tools-store";
import { ArcType, CallbackProperty, Color, ConstantProperty, HeightReference, PointGraphics, PolylineGraphics, PropertyBag, ScreenSpaceEventType, Cartesian3, Entity, ConstantPositionProperty, Cartesian2, defined, JulianDate, HorizontalOrigin, NearFarScalar, SceneMode, CallbackPositionProperty, PolylineDashMaterialProperty, PositionProperty } from "cesium";
import { v4 as uuidv4 } from 'uuid';
import { EntityTypes, getPolylineDistanceInMeters } from "../map-utils";
import { toast } from "$lib/components/organisms/toast/toast";
import { ENTITY_ID_PREFIX } from "$lib/utils/constants";


/************************************************
**********   POLYLINE DRAWING TOOL   ************
************************************************/

const toggleDrawPolylineOnMap = (): void => {
	const viewer = useCesiumViewer();
	const { scene, screenSpaceEventHandler } = viewer;
	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);
	const { sandbox_PolylineDataSource } = useDataSources();

	if (!viewer.dataSources.contains(sandbox_PolylineDataSource)) {
		viewer.dataSources.add(sandbox_PolylineDataSource);
	}

	mapToolsStore.disableAllToolsExceptToggleToolByName('drawPolyline', viewer);

	// Deselect any potentially selected entity at the time of enabling the line drawing tool
	viewer.selectedEntity = undefined;
	// Implementation heavily based on https://sandcastle.cesium.com/index.html?src=Drawing%20on%20Terrain.html

	const createPolylinePoint = (worldPosition: Cartesian3) => {
        const lineId = get(mapToolsStore).drawPolyline.lineId;
		return sandbox_PolylineDataSource.entities.add({
			id: `sandbox_polyline_point:${lineId}`,
			position: worldPosition,
			properties: new PropertyBag({
				entityType: EntityTypes.SANDBOX_POLYLINE_PART,
				lineId
			}),
			point: new PointGraphics({
				color: Color.WHITE,
				pixelSize: 5,
				heightReference: HeightReference.CLAMP_TO_GROUND
			})
		});
	};

	const drawPolylineLineShape = ({
		terminate
	}: {
		terminate: boolean;
	}) => {
        const {lineId} = get(mapToolsStore).drawPolyline;
        const dynamicPositions = new CallbackProperty(() => get(mapToolsStore).drawPolyline.activeShapePoints, false);
        
		if (
			terminate &&
			sandbox_PolylineDataSource.entities.getById(`sandbox_polyline:${lineId}`)
		) {
			const sandboxPolylineEntity = sandbox_PolylineDataSource.entities.getById(
				`sandbox_polyline:${lineId}`
			);

			if (sandboxPolylineEntity?.polyline?.positions) {
                const finalPositions = [...get(mapToolsStore).drawPolyline.activeShapePoints]
                sandboxPolylineEntity.polyline.positions = new ConstantProperty(finalPositions);
			}

			return;
		}

		const polylineMaterial =
			get(mapToolsStore).drawPolyline.lineType === 'SOLID'
				? get(mapToolsStore).drawPolyline.lineColor
				: new PolylineDashMaterialProperty({
						color: get(mapToolsStore).drawPolyline.lineColor,
						dashLength: 12
				  });

		return sandbox_PolylineDataSource.entities.add({
			id: `sandbox_polyline:${lineId}`,
			properties: new PropertyBag({
				entityType: EntityTypes.SANDBOX_POLYLINE,
				lineId: lineId
			}),
			polyline: new PolylineGraphics({
				positions: dynamicPositions,
				width: 5,
				arcType: ArcType.RHUMB,
				material: polylineMaterial || Color.YELLOW,
				clampToGround: true
			})
		});
	};

	const terminatePolylineShape = () => {
        
        mapToolsStore.update(store => {
            store.drawPolyline.activeShapePoints.pop();
            return store;
        })

		drawPolylineLineShape({ terminate: true });
        const floatingPoint = get(mapToolsStore).drawPolyline.floatingPoint as Entity;
		sandbox_PolylineDataSource.entities.remove(floatingPoint);
		//sandbox_PolylineDataSource.entities.remove(activeShape as Entity);
		get(mapToolsStore).drawPolyline.cornerPoints.forEach((cornerPoint) => sandbox_PolylineDataSource.entities.remove(cornerPoint));

        resetStoreValues('drawing');
	};

	if (get(mapToolsStore).drawPolyline.toolEnabled) {
		viewer.scene.screenSpaceCameraController.enableTranslate = false;
		viewer.scene.screenSpaceCameraController.enableLook = false;

		toggleEscapeToolListener({toggleOn: true});
		disableDefaultLeftClickHandler(screenSpaceEventHandler);

		screenSpaceEventHandler.setInputAction((event: { endPosition: Cartesian2 }) => {
            const floatingPoint = get(mapToolsStore).drawPolyline.floatingPoint;
			if (floatingPoint && defined(floatingPoint)) {
				let earthPosition: Cartesian3 | undefined;

				const pickedObject = scene.pick(event.endPosition);

				// If the user hovers a mastpoint entity in the map, retrieve the entity's position
				if (pickedObject && defined(pickedObject)) {
					const entity: Entity = pickedObject.id;

					/* const { entityType } =
						getEntityPropertyBag<IMastPointFullModel.MastPointPropertyBagModel>(entity); */

					if (entity && entity.id.includes(ENTITY_ID_PREFIX.MASTPOINT)) {
						// Set the moving line's position to the clicked entity's position
						earthPosition = entity.position?.getValue(new JulianDate());
					}
				}

				if (typeof earthPosition === 'undefined' || !earthPosition) {
					earthPosition = viewer.scene.pickPosition(event.endPosition);
				}

				if (defined(earthPosition)) {
                    mapToolsStore.update(store => {
                        // We actually check this further up in the code, but we need to set it here to avoid errors.
                        store.drawPolyline.floatingPoint!.position = new CallbackPositionProperty(() => earthPosition, false);
                        store.drawPolyline.activeShapePoints.pop();
                        store.drawPolyline.activeShapePoints = [...store.drawPolyline.activeShapePoints, earthPosition];
                        return store;
                    });
				}
			}
		}, ScreenSpaceEventType.MOUSE_MOVE);

		screenSpaceEventHandler.setInputAction(() => {
            const {activeShapePoints} = get(mapToolsStore).drawPolyline;
            if(activeShapePoints.length > 2){
                terminatePolylineShape();
            }else{
                if (activeShapePoints.length === 2) {
					toast({
						type: 'warning',
						title: 'Tegning avbrutt',
						description: `Du må ha minst 2 punkter for å tegne en linje.`
					});
                }
                removeTemporaryEntities();
                resetStoreValues('drawing');
            }
		}, ScreenSpaceEventType.RIGHT_CLICK);

		// Enable adding line Entities using SHIFT+CLICK
		screenSpaceEventHandler.setInputAction((event: { position: Cartesian2 }) => {
			let earthPosition: Cartesian3 | undefined;
			const pickedObject = scene.pick(event.position);

			// If the user hovers a mastpoint entity in the map, retrieve the entity's position
			if (pickedObject && defined(pickedObject)) {
				const entity: Entity = pickedObject.id;

				if (entity.id.includes(ENTITY_ID_PREFIX.MASTPOINT)) {
					// Set the moving line's position to the clicked entity's position
					earthPosition = entity.position?.getValue(new JulianDate());
				}
			}

			if (typeof earthPosition === 'undefined' || !earthPosition) {
				if (scene.mode === SceneMode.SCENE3D) {
					earthPosition = viewer.scene.pickPosition(event.position); // Correct point when mousing over terrain
				} else if (scene.mode === SceneMode.SCENE2D) {
					earthPosition = viewer.camera.pickEllipsoid(event.position); // Good for 2D (non-terrain)
				} else {
					alert(
						'INFO: 2.5D mode is not good for placing elements on map. Use 2D or 3D. No element was placed.'
					);
					earthPosition = undefined;
				}
			}

			// `earthPosition` will be undefined if our mouse is not over the globe.
			if (earthPosition && defined(earthPosition)) {

                if (get(mapToolsStore).drawPolyline.activeShapePoints.length === 0) {
                    mapToolsStore.update(store => {
                        let currentShapePoints = store.drawPolyline.activeShapePoints;
                        store.drawPolyline.activeShapePoints = [...currentShapePoints, earthPosition as Cartesian3];
                        store.drawPolyline.lineId = uuidv4();
                        store.drawPolyline.floatingPoint = createPolylinePoint(earthPosition as Cartesian3);
                        store.drawPolyline.activeShape = drawPolylineLineShape({ terminate: false });
                        return store;
                    });
				}
                // If activeShapePoints already exists, add a single new point. If it does not exist, create an additional point. One point for the click origin, one for the mouse position.
                // This is to ensure that the line is drawn dynamically whenever the user moves the mouse (At least 2 points).
                mapToolsStore.update(store => {
                    let currentShapePoints = store.drawPolyline.activeShapePoints;
                    store.drawPolyline.activeShapePoints = [...currentShapePoints, earthPosition as Cartesian3];
                    return store;
                })
				earthPosition = undefined;
			}
		}, ScreenSpaceEventType.LEFT_CLICK);
	} else {
        const activeShapePoints = [...get(mapToolsStore).drawPolyline.activeShapePoints];
        if(activeShapePoints.length > 2){
            terminatePolylineShape();
        }else{
            if (activeShapePoints.length === 2) {
				toast({
					type: 'warning',
					title: 'Tegning avbrutt',
					description: `Du må ha minst 2 punkter for å tegne en linje.`
				});
            }
            removeTemporaryEntities();
            resetStoreValues('drawing');
        }
		toggleEscapeToolListener({toggleOn: false});
		// If the user disables the "add pole" tool, remove the input action event listener
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);

		viewer.scene.screenSpaceCameraController.enableTranslate = true;
		viewer.scene.screenSpaceCameraController.enableLook = true;
	}
};


/************************************************
********** DISTANCE MEASUREMENT TOOL ************
************************************************/

/**
 * Tool for 2D and 3D distance measurement lines. 3D lines are straight lines between points, while 2D is along terrain and objects.
 * @param shouldClampToGround - If true, the distance line will be clamped to the ground (2D). If false, the distance line will be drawn in 3D space.
 */
const toggleDrawDistanceLineOnMap = (
	shouldClampToGround?: boolean
): void => {
	const viewer = useCesiumViewer();
	const { scene, screenSpaceEventHandler } = viewer;
	const { measureDistancePolylineDataSource } = useDataSources();

    let lastDistUpdate = 0;

	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);
    mapToolsStore.update(store => {
        store.drawMeasurementLine.shouldClampToGround = shouldClampToGround ?? false;
        return store;
    })

	if (!viewer.dataSources.contains(measureDistancePolylineDataSource)) {
		viewer.dataSources.add(measureDistancePolylineDataSource);
	}

	// Deselect any potentially selected entity at the time of enabling the measurement line drawing tool. Disable all tools should not be a relevant function anymore
	mapToolsStore.disableAllToolsExceptToggleToolByName('drawMeasurementLine', viewer);
	viewer.selectedEntity = undefined;

	// Implementation heavily based on https://sandcastle.cesium.com/index.html?src=Drawing%20on%20Terrain.html
	const createPoint = (worldPosition: Cartesian3) => {
		return measureDistancePolylineDataSource.entities.add({
			position: worldPosition,
			properties: new PropertyBag({
				entityType: EntityTypes.DISTANCE_MEASURE_LINE_PART,
				lineId: get(mapToolsStore).drawMeasurementLine.lineId
			}),
			point: new PointGraphics({
				color: Color.WHITE,
				pixelSize: 5,
				heightReference: HeightReference.CLAMP_TO_GROUND
			})
		});
	};

	if (get(mapToolsStore).drawMeasurementLine.toolEnabled) {
        // Enable the escape button to terminate the distance line drawing tool and disable default left click behaviour.
		toggleEscapeToolListener({toggleOn: true});
		disableDefaultLeftClickHandler(screenSpaceEventHandler);

        // Make sure we disable controls that may interfere with the distance line drawing tool.
		viewer.scene.screenSpaceCameraController.enableTranslate = false;
		viewer.scene.screenSpaceCameraController.enableLook = false;

        // On movement of the mouse, we will update the position of the floating point (floatingPoint), the label (labelEntity) and the distance line (activeShape and activeShapePoints).
		screenSpaceEventHandler.setInputAction(async (event: { endPosition: Cartesian2 }) => {
            
            // Get the floating point from the mapToolsStore. This is created in the LEFT_CLICK-handler, and should exist at this point. 
            // This is the point that will be moved around as the user moves the mouse. 
            const {floatingPoint} = get(mapToolsStore).drawMeasurementLine;
			if (floatingPoint && defined(floatingPoint)) {
				let earthPosition: Cartesian3 | undefined;
                
				// If the user is in regular distance measurement line mode, clicking on a mastpoint entity should make use of the entity's position for the measurement line.
				// Otherwise, the user is in "3D måleverktøy" mode, and the drawn line could be placed anywhere on the mastpoint, hence not making use of the mastpoint entity's position.
				if (shouldClampToGround) {
					const pickedObject = scene.pick(event.endPosition);
					// If the user hovers a mastpoint entity in the map, retrieve the entity's position
					if (pickedObject && defined(pickedObject)) {
						const entity: Entity = pickedObject.id;
						if (entity && entity.id.includes(ENTITY_ID_PREFIX.MASTPOINT)) {
							// Set the moving line's position to the clicked entity's position
							earthPosition = entity.position?.getValue(new JulianDate());
						}
					}
				}

                // If no entity is hit, pick a world position.
				if (typeof earthPosition === 'undefined' || !earthPosition) {
					earthPosition = viewer.scene.pickPosition(event.endPosition);
				}

                // `earthPosition` will be undefined if our mouse is not over the globe.
				if (defined(earthPosition)) {
                    // Update store with the new position of the floating point.
                    mapToolsStore.update(store => {
                        
                        let currentShapePoints = store.drawMeasurementLine.activeShapePoints;
                        // We actually check this further up in the code, but we need to set it here to avoid errors.
                        store.drawMeasurementLine.floatingPoint!.position = new ConstantPositionProperty(earthPosition);
                        currentShapePoints.pop();
                        store.drawMeasurementLine.activeShapePoints = [...currentShapePoints, earthPosition as Cartesian3];
                       
                        
                        return store;
                    });

                    // Get values after store is updated (necessary?)
                    const {lineId, labelEntity, activeShapePoints} = get(mapToolsStore).drawMeasurementLine;

                    // Create the label entity if it does not exist, or update the position and text of the existing label entity.
					if (!labelEntity) {
						const newPositionLabelEntity = measureDistancePolylineDataSource.entities.add({
							id: `distance_polyline_label:${lineId}`,
							properties: new PropertyBag({
								entityType: EntityTypes.DISTANCE_MEASURE_LINE_PART,
								lineId
							}),
							label: {
								show: true,
								showBackground: true,
								font: '15px Poppins',
								eyeOffset: new Cartesian3(0.0, 0.0, -10.0),
								horizontalOrigin: HorizontalOrigin.CENTER,
								pixelOffset: new Cartesian2(0.0, -20.0),
								pixelOffsetScaleByDistance: new NearFarScalar(1.5e2, 3.0, 1.5e7, 0.5),
								disableDepthTestDistance: 1.2742018 * 10 ** 7
							}
						});

						mapToolsStore.update(store => {
							store.drawMeasurementLine.labelEntity = newPositionLabelEntity;
							return store;
						})
					} else {
                        const now = performance.now();
                        if (now - lastDistUpdate > 30) {
                            lastDistUpdate = now;
                            
                            labelEntity.position = new ConstantPositionProperty(earthPosition);
                            const { totalDistanceInMeters } = await getPolylineDistanceInMeters(
                                activeShapePoints,
                                viewer,
                                shouldClampToGround
                            );
                            if (labelEntity.label) {
                                labelEntity.label.text = new ConstantProperty(`Avstand: ${totalDistanceInMeters.toFixed(2)} m`);
                            }
                        }
                    }
				}
			}
		}, ScreenSpaceEventType.MOUSE_MOVE);

        // Right click will terminate the distance line.
		screenSpaceEventHandler.setInputAction((event: ScreenSpaceEventType) => {
            const {activeShapePoints} = get(mapToolsStore).drawMeasurementLine;
            if(activeShapePoints.length > 2){
                terminateShape();
            }else{
                if (activeShapePoints.length === 2) {
					toast({
						type: 'warning',
						title: 'Tegning avbrutt',
						description: `Du må ha minst 2 punkter for å tegne en linje.`
					});
                }
                removeTemporaryEntities();
                resetStoreValues('measurement');
            }
		}, ScreenSpaceEventType.RIGHT_CLICK);

		// Left click will add a new point to the distance line.
		screenSpaceEventHandler.setInputAction((event: { position: Cartesian2 }) => {
			let earthPosition: Cartesian3 | undefined;
            
			// If the user is in regular distance measurement line mode, clicking on a mastpoint entity should make use of the entity's position for the measurement line.
			// Otherwise, the user is in "3D måleverktøy" mode, and the drawn line could be placed anywhere on the mastpoint, hence not making use of the mastpoint entity's position.
			if (shouldClampToGround) {
				const pickedObject = scene.pick(event.position);

				// If the user click's a mastpoint entity in the map, retrieve the entity's position
				if (pickedObject && defined(pickedObject)) {
					const entity: Entity = pickedObject.id;

					if (entity && entity.id.includes(ENTITY_ID_PREFIX.MASTPOINT)) {
						// Set the moving line's position to the clicked entity's position
						earthPosition = entity.position?.getValue(new JulianDate());
					}
				}
			}


			if (typeof earthPosition === 'undefined' || !earthPosition) {
				if (scene.mode === SceneMode.SCENE3D) {
					earthPosition = viewer.scene.pickPosition(event.position); // Correct point when mousing over terrain
				} else if (scene.mode === SceneMode.SCENE2D) {
					earthPosition = viewer.camera.pickEllipsoid(event.position); // Good for 2D (non-terrain)
				} else {
					alert(
						'INFO: 2.5D mode is not good for placing elements on map. Use 2D or 3D. No element was placed.'
					);
					earthPosition = undefined;
				}
			}

			// `earthPosition` will be undefined if our mouse is not over the globe.
			if (earthPosition && defined(earthPosition)) {
				if (get(mapToolsStore).drawMeasurementLine.activeShapePoints.length === 0) {
                    mapToolsStore.update(store => {
                        let currentShapePoints = store.drawMeasurementLine.activeShapePoints;
                        store.drawMeasurementLine.activeShapePoints = [...currentShapePoints, earthPosition as Cartesian3];
                        store.drawMeasurementLine.lineId = uuidv4();
                        store.drawMeasurementLine.floatingPoint = createPoint(earthPosition as Cartesian3);
                        store.drawMeasurementLine.activeShape = drawLineShape({ terminate: false });
                        return store;
                    });
				}
                // If activeShapePoints already exists, add a single new point. If it does not exist, create an additional point. One point for the click origin, one for the mouse position.
                // This is to ensure that the line is drawn dynamically whenever the user moves the mouse (At least 2 points).
                mapToolsStore.update(store => {
                    let currentShapePoints = store.drawMeasurementLine.activeShapePoints;
                    store.drawMeasurementLine.activeShapePoints = [...currentShapePoints, earthPosition as Cartesian3];
                    return store;
                })
				//createPoint(earthPosition);
				//earthPosition = undefined;
			}
		}, ScreenSpaceEventType.LEFT_CLICK);
	} else {
        const {activeShapePoints} = get(mapToolsStore).drawMeasurementLine;
            if(activeShapePoints.length > 2){
                terminateShape();
            }else{
                if (activeShapePoints.length === 2) {
					toast({
						type: 'warning',
						title: 'Tegning avbrutt',
						description: `Du må ha minst 2 punkter for å tegne en linje.`
					});
                }
                removeTemporaryEntities();
                resetStoreValues('measurement');
            }
		toggleEscapeToolListener({toggleOn: false});
		viewer.scene.screenSpaceCameraController.enableTranslate = true;
		viewer.scene.screenSpaceCameraController.enableLook = true;

		// If the user disables the "add pole" tool, remove the input action event listener
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
	}
};

/**
 * Draws the line shape based on the provided position data.
 * If `terminate` is true, it updates the existing line entity with the new positions.
 * @param param0 
 * @returns 
 */
const drawLineShape = ({
    terminate
}: {
    terminate: boolean;
}) => {
    const { measureDistancePolylineDataSource } = useDataSources();
    const {lineId, shouldClampToGround} = get(mapToolsStore).drawMeasurementLine;
    const dynamicPositions = new CallbackProperty(() => {
        return get(mapToolsStore).drawMeasurementLine.activeShapePoints
        }, false);
    if (
        terminate &&
        measureDistancePolylineDataSource.entities.getById(
            `distance_polyline:${lineId}`
        )
    ) {
        const measureDistanceLineEntity = measureDistancePolylineDataSource.entities.getById(
            `distance_polyline:${lineId}`
        );
        if (measureDistanceLineEntity?.polyline?.positions) {
            const finalPositions = [...get(mapToolsStore).drawMeasurementLine.activeShapePoints]
            measureDistanceLineEntity.polyline.positions = new ConstantProperty(finalPositions);
        }
        return;
    }

    const entity = measureDistancePolylineDataSource.entities.add({
        id: `distance_polyline:${lineId}`,
        properties: new PropertyBag({
            entityType: EntityTypes.DISTANCE_MEASURE_LINE_PART,
            lineId
        }),
        polyline: new PolylineGraphics({
            positions: dynamicPositions,
            width: 3,
            arcType: ArcType.RHUMB,
            material: Color.YELLOW,
            clampToGround: shouldClampToGround
        })
    });

    return entity;
};

/**
 * Terminates the shape by removing the last point, updating the label entity's position, and restoring mapToolsStore to default values.
 */
const terminateShape = async () => {

    const viewer = useCesiumViewer();
    const {measureDistancePolylineDataSource} = useDataSources();

    mapToolsStore.update(store => {
        store.drawMeasurementLine.activeShapePoints.pop();
        return store;
    })

    const {activeShapePoints, labelEntity, shouldClampToGround} = get(mapToolsStore).drawMeasurementLine;

    if(labelEntity instanceof Entity) {
        // Always set the label's position to the last element in the distance line array, and update the distance label to display the correct distance for the finalized polyline.
        labelEntity.position = new ConstantPositionProperty(activeShapePoints.at(-1));
        //const { totalDistanceInMeters, totalDistanceIn3D } = getPolylineDistanceInMeters(activeShapePoints);
        //const { totalDistanceInMeters } = getPolylineDistanceInMeters(activeShapePoints);
        const { totalDistanceInMeters } = await getPolylineDistanceInMeters(
            activeShapePoints,
            viewer,
            shouldClampToGround
        );

        //const totalDistance = shouldClampToGround ? totalDistanceInMeters : totalDistanceIn3D;

        if (labelEntity.label) {
            labelEntity.label.text = new ConstantProperty(
                'Avstand: ' + totalDistanceInMeters.toFixed(2) + ' m'
            );
        }
    }

    drawLineShape({ terminate: true });
    const floatingPoint = get(mapToolsStore).drawMeasurementLine.floatingPoint;
    measureDistancePolylineDataSource.entities.remove(floatingPoint as Entity);
    // Reset store values
    resetStoreValues('measurement');
};

const removeTemporaryEntities = () => {
    const {measureDistancePolylineDataSource} = useDataSources();
    const {drawMeasurementLine} = get(mapToolsStore);
    if (drawMeasurementLine.floatingPoint) {
        measureDistancePolylineDataSource.entities.remove(drawMeasurementLine.floatingPoint);
    }
    if (drawMeasurementLine.labelEntity) {
        measureDistancePolylineDataSource.entities.remove(drawMeasurementLine.labelEntity);
    }
    if (drawMeasurementLine.activeShape) {
        measureDistancePolylineDataSource.entities.remove(drawMeasurementLine.activeShape);
    }
}

const resetStoreValues = (type: 'drawing' | 'measurement') => {
    if(type === 'measurement'){
    mapToolsStore.update(store => {
        store.drawMeasurementLine.labelEntity = undefined;
        store.drawMeasurementLine.activeShapePoints = [];
        store.drawMeasurementLine.lineId = undefined;
        store.drawMeasurementLine.floatingPoint = undefined;
        store.drawMeasurementLine.activeShape = undefined;

        return store;
    });
} else if(type === 'drawing'){
    mapToolsStore.update(store => {
        store.drawPolyline.activeShapePoints = [];
        store.drawPolyline.activeShape = undefined;
        store.drawPolyline.floatingPoint = undefined;
        store.drawPolyline.cornerPoints = [];
        store.drawPolyline.lineId = undefined;

        return store;
    });
}
}

export {
	toggleDrawDistanceLineOnMap,
    toggleDrawPolylineOnMap
}